import { DatePicker, TimePicker, Input, Select, InputNumber } from "antd"
import dayjs from 'dayjs';
import { toDatePickerValue } from '../../../utils/safeDate.js';
import UserRoleDropdown from "../../UserRoleDropdown";
import { useTranslation } from 'react-i18next';
import { useMasterOptionLabel } from "../../../hooks/useMasterOptionLabel";
import { isCompatibleDynamicSetSource } from "../../../utils/formsHelper";

const SetValue = ({ action, actionIndex, setElementValue, selectedElement, formElements = [] }) => {
    const { t } = useTranslation();
    const { getOptionLabel } = useMasterOptionLabel();
    let formType = selectedElement?.formType || "inputForm"
    const valueSource = action?.valueSource || "static"


    const handleChange = (val) => {
        if (formType === "multipleChoiceField") {
            val = Array.isArray(val) ? JSON.stringify(val) : val
        }
        setElementValue(val, actionIndex)
    }

    const handleValueSourceChange = (val) => {
        setElementValue(val, actionIndex, "valueSource")
        if (val === "static") {
            setElementValue(null, actionIndex, "dynamicField")
        }
    }

    const dynamicFieldOptions = formElements
        .filter((element) => element.id !== selectedElement?.id && isCompatibleDynamicSetSource(element, selectedElement))
        .map((element) => ({
            label: element.label || element.name || "Untitled Field",
            value: element.id
        }))
    if (formType === "dateForm") {
        dynamicFieldOptions.unshift({ label: t('Today'), value: "$TODAY" })
    }

    const onSelect = (values) => {
        let reqValues = values
        if (formType === "userForm") {
            reqValues = { ...values }
            reqValues = JSON.stringify(reqValues)
        }
        setElementValue(reqValues, actionIndex)

    }

    const renderStaticComponent = () => {
        let currentComponent = null;
        if (formType === "inputForm" || formType === "addressField" || formType === "contactField" || formType === "textAreaForm" || formType === "linkField") {
            currentComponent = <Input placeholder={t('Enter value')} value={action.value} onChange={(e) => handleChange(e.target.value)} style={{ minWidth: "8.5rem", maxWidth: "8.5rem" }} />
        } else if (formType === "numberForm") {
            currentComponent = <InputNumber placeholder={t('Enter value')} value={action.value} onChange={(val) => handleChange(val)} style={{ minWidth: "8.5rem", maxWidth: "8.5rem" }} />

        } else if (formType === "radioField" || formType === "multipleChoiceField" || formType === "dropdownField") {
            let dropValue = action.value;
            if (dropValue && (formType === "multipleChoiceField")) {
                try {
                    dropValue = JSON.parse(dropValue)
                } catch {
                    dropValue = []
                }
            }
            let reqOptions = []
            if (selectedElement.options) {
                reqOptions = selectedElement.options.map(opt => {
                    // A master backed option can carry a row uuid as its label
                    return { label: getOptionLabel(selectedElement, opt), value: opt.value }
                })
            }
            currentComponent = <Select {...((formType === "multipleChoiceField") && { mode: "multiple" })} disabled={false} placeholder={t('Select')} value={dropValue} options={reqOptions} onChange={(val) => handleChange(val)} style={{ minWidth: "8.5rem", maxWidth: "8.5rem", textAlign: "left" }} />
        } else if (formType === "dateForm") {
            const isDynamicValue = action.value === "$TODAY";
            const isStaticMode = action.value === "$STATIC" || (action.value && action.value !== "$TODAY");
            const dateValueOptions = [
                { label: t('Today (Dynamic)'), value: "$TODAY" },
                { label: t('Static Date'), value: "$STATIC" }
            ];

            let selectedOption = undefined;
            if (isDynamicValue) {
                selectedOption = "$TODAY";
            } else if (isStaticMode) {
                selectedOption = "$STATIC";
            }

            currentComponent = (
                <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <Select
                        placeholder={t('Value type')}
                        options={dateValueOptions}
                        value={selectedOption}
                        onChange={(val) => {
                            if (val === "$TODAY") {
                                handleChange("$TODAY");
                            } else {
                                handleChange("$STATIC");
                            }
                        }}
                        style={{ minWidth: "6.5rem", maxWidth: "6.5rem" }}
                    />
                    {isStaticMode && (
                        <DatePicker
                            placeholder={t('Select')}
                            value={action.value && action.value !== "$TODAY" && action.value !== "$STATIC" ? toDatePickerValue(action.value) : null}
                            format="YYYY-MM-DD"
                            onChange={(date, dateString) => handleChange(dateString || "$STATIC")}
                            style={{ minWidth: "7rem", maxWidth: "7rem" }}
                        />
                    )}
                </div>
            );
        } else if (formType === "timeForm") {
            currentComponent = <TimePicker placeholder={t('Select time')} value={toDatePickerValue(action.value)} format={"HH:mm"} use12Hours={false} onChange={(value) => handleChange(value)} style={{ minWidth: "8.5rem", maxWidth: "8.5rem" }} />

        } else if (formType === "switchForm") {
            currentComponent = <Select disabled={false} placeholder={t('Select')} value={action.value} options={[{ label: "on", value: "on" }, { label: "off", value: "off" }]} onChange={(val) => handleChange(val)} style={{ minWidth: "8.5rem", maxWidth: "8.5rem", textAlign: "left" }} />

        } else if (formType === "checkboxField") {
            currentComponent = <Select disabled={false} placeholder={t('Select')} value={action.value} options={[{ label: "checked", value: "on" }, { label: "unchecked", value: "off" }]} onChange={(val) => handleChange(val)} style={{ minWidth: "8.5rem", maxWidth: "8.5rem", textAlign: "left" }} />

        } else if (formType === "userForm") {
            let actionOptions = typeof action.value === "string" ? JSON.parse(action.value) : action.value
            const dropdownFields = formElements.filter((element) => element.formType === "dropdownField")
            currentComponent = <div style={{ minWidth: "8.5rem", maxWidth: "8.5rem" }}>
                <UserRoleDropdown
                    className="compact-user-dropdown"
                    selectedOptions={{
                        user: actionOptions?.user ? actionOptions.user : [],
                        group: actionOptions?.group ? actionOptions.group : [],
                        role: [],
                        dropdownItem: actionOptions?.dropdownItem || []
                    }}
                    formElements={dropdownFields}
                    onSelect={onSelect}
                    tabsRequired={["user", "group", "dropdownItem"]}
                    getSpaceUsers={true} dropdownStyle={{ width: "15rem" }} />
            </div>
        }
        return currentComponent
    }

    return (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "0.5rem" }}>
            <Select
                placeholder={t('Value type')}
                value={valueSource}
                options={[
                    { label: t('Static'), value: "static" },
                    { label: t('Dynamic'), value: "dynamic" }
                ]}
                onChange={handleValueSourceChange}
                style={{ minWidth: "8.5rem", width: "8.5rem", textAlign: "left" }}
            />
            {
                valueSource === "dynamic"
                    ? <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder={t('Select field')}
                        value={action.dynamicField}
                        options={dynamicFieldOptions}
                        onChange={(val) => setElementValue(val, actionIndex, "dynamicField")}
                        style={{ minWidth: "8.5rem", width: "8.5rem", textAlign: "left" }}
                    />
                    : renderStaticComponent()
            }
        </div>
    )
}

export default SetValue
